import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';
import { useAlert } from '../hooks/useAlert';

interface Project {
  id: string;
  name: string;
  html_template: string;
  editor_data?: {
    elements: any[];
  };
}

interface UserStats {
  total_api_generations: number;
  total_runner_downloads: number;
  total_generations: number;
  projects_count: number;
}

interface UserCredits {
  total_credits: number;
  used_credits: number;
  remaining_credits: number;
  last_updated: string;
}

interface UserApiToken {
  api_token: string | null;
  token_created_at: string | null;
}

const ApiTokensPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [apiToken, setApiToken] = useState<UserApiToken | null>(null);
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const { showAlert, AlertComponent } = useAlert();

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      // Fetch user's API token from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('api_token, token_created_at')
        .eq('id', user!.id)
        .single();

      if (userError) throw userError;
      
      setApiToken({
        api_token: userData.api_token,
        token_created_at: userData.token_created_at
      });

      // Fetch user credits using new FIFO system
      const { data: creditsValue } = await supabase.rpc('get_user_available_credits', {
        p_user_id: user!.id
      });
      
      const availableCredits = creditsValue || 0;
      
      setCredits({ 
        remaining_credits: availableCredits,
        total_credits: availableCredits,
        used_credits: 0,
        last_updated: new Date().toISOString()
      });

      // Fetch projects with editor_data
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('id, name, html_template, editor_data')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;
      setProjects(projectsData || []);
      if (projectsData && projectsData.length > 0) {
        setSelectedProject(projectsData[0].id);
      }

      // Fetch user stats
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_user_stats', { p_user_id: user!.id });

      if (!statsError && statsData && statsData.length > 0) {
        setStats(statsData[0]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetToken = async () => {
    if (!window.confirm(t('pages.apiTokens.resetConfirm'))) {
      return;
    }

    setResetting(true);
    try {
      // Generate new token
      const newToken = `audome_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;
      const now = new Date().toISOString();

      // Update user's API token
      const { error } = await supabase
        .from('users')
        .update({ 
          api_token: newToken,
          token_created_at: now
        })
        .eq('id', user!.id);

      if (error) throw error;

      showAlert({
        title: t('pages.apiTokens.success'),
        message: t('pages.apiTokens.tokenRegenerated'),
        type: 'success'
      });

      fetchData();
    } catch (err: any) {
      showAlert({
        title: t('pages.apiTokens.resetFailed'),
        message: err.message,
        type: 'error'
      });
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showAlert({
      title: t('pages.apiTokens.success'),
      message: t('pages.apiTokens.tokenCopied'),
      type: 'success'
    });
  };

  if (loading) {
    return (
      <>
        <AlertComponent />
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">{t('pages.apiTokens.loading')}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <AlertComponent />
      <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
        <AppHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('pages.apiTokens.title')}</h1>
          <p className="text-gray-400">{t('pages.apiTokens.subtitle')}</p>
        </div>

        {/* Credits Display */}
        {credits && (
          <div className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/40 border-2 border-yellow-500/50 rounded-2xl p-8 mb-8 text-center">
            <div className="text-yellow-400 text-sm font-semibold mb-2">{t('pages.apiTokens.remainingCredits')}</div>
            <div className="text-6xl font-bold text-white mb-2">{credits.remaining_credits}</div>
            <div className="text-yellow-300 text-sm">
              {t('pages.apiTokens.fromTotal', { total: credits.total_credits })}
            </div>
            {credits.remaining_credits <= 0 && (
              <div className="mt-4 bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
                {t('pages.apiTokens.noCredits')}
              </div>
            )}
            {credits.remaining_credits > 0 && credits.remaining_credits <= 10 && (
              <div className="mt-4 bg-orange-500/20 border border-orange-500/30 rounded-lg p-3 text-orange-300 text-sm">
                {t('pages.apiTokens.lowCredits', { credits: credits.remaining_credits })}
              </div>
            )}
          </div>
        )}

        {/* Usage Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border border-blue-500/30 rounded-xl p-6">
              <div className="text-blue-400 text-sm mb-2">{t('pages.apiTokens.totalImages')}</div>
              <div className="text-3xl font-bold text-white">{stats.total_generations}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 border border-purple-500/30 rounded-xl p-6">
              <div className="text-purple-400 text-sm mb-2">{t('pages.apiTokens.viaApi')}</div>
              <div className="text-3xl font-bold text-white">{stats.total_api_generations}</div>
            </div>
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/30 border border-green-500/30 rounded-xl p-6">
              <div className="text-green-400 text-sm mb-2">{t('pages.apiTokens.viaRunner')}</div>
              <div className="text-3xl font-bold text-white">{stats.total_runner_downloads}</div>
            </div>
            <div className="bg-gradient-to-br from-orange-900/30 to-orange-800/30 border border-orange-500/30 rounded-xl p-6">
              <div className="text-orange-400 text-sm mb-2">{t('pages.apiTokens.projects')}</div>
              <div className="text-3xl font-bold text-white">{stats.projects_count}</div>
            </div>
          </div>
        )}

        {/* API Token Display */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">{t('pages.apiTokens.currentToken')}</h2>
            {apiToken?.api_token && (
              <button
                onClick={resetToken}
                disabled={resetting}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                {resetting ? t('pages.apiTokens.resetting') : t('pages.apiTokens.resetToken')}
              </button>
            )}
          </div>

          {!apiToken?.api_token ? (
            <div className="text-center py-8 text-gray-400">
              {t('pages.apiTokens.noToken')}
              <div className="mt-4">
                <button
                  onClick={resetToken}
                  disabled={resetting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  {resetting ? t('pages.apiTokens.creating') : t('pages.apiTokens.createToken')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">{t('pages.apiTokens.tokenLabel')}</label>
                <div className="bg-black rounded-lg p-4 border border-zinc-700 flex items-center justify-between gap-4">
                  <code className="text-green-400 text-sm break-all flex-1 font-mono">{apiToken.api_token}</code>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(apiToken.api_token!)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap flex items-center gap-2"
                      title={t('pages.apiTokens.copyTitle')}
                    >
                      {t('pages.apiTokens.copy')}
                    </button>
                    <button
                      onClick={resetToken}
                      disabled={resetting}
                      className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
                      title={t('pages.apiTokens.editTitle')}
                    >
                      {t('pages.apiTokens.edit')}
                    </button>
                  </div>
                </div>
              </div>
              
              {apiToken.token_created_at && (
                <div className="text-sm text-gray-400">
                  <div>{t('pages.apiTokens.createdAt')} {new Date(apiToken.token_created_at).toLocaleDateString('en-US')} {new Date(apiToken.token_created_at).toLocaleTimeString('en-US')}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CURL Generator */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">{t('pages.apiTokens.curlGenerator')}</h2>
          
          {apiToken?.api_token && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('pages.apiTokens.selectProject')}</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-4 py-3 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="">{t('pages.apiTokens.selectProjectPlaceholder')}</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {apiToken?.api_token && selectedProject && (() => {
            const project = projects.find(p => p.id === selectedProject);
            const placeholders: string[] = [];
            if (project && project.html_template) {
              const matches = project.html_template.match(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g);
              if (matches) {
                matches.forEach(match => {
                  const key = match.replace(/\{\{|\}\}/g, '').trim();
                  if (!placeholders.includes(key)) placeholders.push(key);
                });
              }
            }

            // Extract real values from editor_data using element names
            const dataObj: any = {};
            const elements = project?.editor_data?.elements || [];
            
            elements.forEach((el: any) => {
              // Skip hidden elements
              if (el.hideInRunner) return;
              
              // استخدم الـ name من العنصر - يكون بالفعل محتوي على text- أو img-
              const key = el.name || (el.type === 'text' ? `text-${el.order !== undefined ? el.order + 1 : 1}` : `img-${el.order !== undefined ? el.order + 1 : 1}`);
              
              if (el.type === 'text') {
                if (placeholders.includes(key)) {
                  dataObj[key] = el.text || '';
                }
              } else if (el.type === 'image') {
                if (placeholders.includes(key)) {
                  dataObj[key] = el.src || '';
                }
              }
            });
            
            // Fill remaining placeholders with examples
            placeholders.forEach(key => {
              if (!dataObj[key]) {
                if (key.startsWith('text')) {
                  dataObj[key] = `مثال ${key}`;
                } else if (key.startsWith('img')) {
                  dataObj[key] = 'https://example.com/image.jpg';
                } else {
                  dataObj[key] = `قيمة ${key}`;
                }
              }
            });

            const curlCommand = `curl -X POST "https://api.render.audome.io/api/generate/${selectedProject}" \\
  -H "Authorization: Bearer ${apiToken.api_token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": ${JSON.stringify(dataObj, null, 6).replace(/\n/g, '\n    ')}
  }'`;

            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-400">{t('pages.apiTokens.customCurl')}</label>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(curlCommand);
                      showAlert({
                        title: t('pages.apiTokens.success'),
                        message: t('pages.apiTokens.commandCopied'),
                        type: 'success'
                      });
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-semibold transition-colors"
                  >
                    {t('pages.apiTokens.copyCommand')}
                  </button>
                </div>
                <pre className="bg-black rounded-lg p-4 overflow-x-auto text-sm border border-zinc-700">
                  <code className="text-green-400">{curlCommand}</code>
                </pre>
                {placeholders.length > 0 && (
                  <div className="mt-3 text-sm text-gray-400">
                    <strong>{t('pages.apiTokens.availablePlaceholders')}:</strong> {placeholders.join(', ')}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* API Documentation */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">{t('pages.apiTokens.howToUse')}</h2>
          <div className="space-y-4 text-gray-300">
            <div>
              <h3 className="font-semibold text-white mb-2">{t('pages.apiTokens.projectsList')}</h3>
              <pre className="bg-black rounded-lg p-4 overflow-x-auto text-sm border border-zinc-700">
{`curl -X GET "https://api.render.audome.io/api/projects" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
              </pre>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">{t('pages.apiTokens.generateImage')}</h3>
              <pre className="bg-black rounded-lg p-4 overflow-x-auto text-sm border border-zinc-700">
{`curl -X POST "https://api.render.audome.io/api/generate/PROJECT_ID" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": {
      "title": "مرحباً بكم",
      "subtitle": "في أودومي",
      "bg": "https://example.com/image.jpg"
    }
  }'`}
              </pre>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">{t('pages.apiTokens.response')}</h3>
              <pre className="bg-black rounded-lg p-4 overflow-x-auto text-sm border border-zinc-700">
{`{
  "success": true,
  "image_url": "https://api.render.audome.io/images/20240115_120000_abc123.png",
  "generation_id": "uuid",
  "credits_remaining": 453,
  "metadata": {
    "width": 800,
    "height": 600,
    "generationTime": 1234
  }
}`}
              </pre>
            </div>
          </div>
        </div>

      </main>

      {/* Footer with API Documentation Link */}
      <footer className="bg-gray-900 border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-lg font-bold text-white mb-4">{t('pages.apiTokens.apiDocs')}</h3>
            <p className="text-gray-400 mb-6">
              {t('pages.apiTokens.apiDocsSubtitle')}
            </p>
            <button
              onClick={() => navigate('/api-docs')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold transition-colors flex items-center gap-2 mx-auto"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              {t('pages.apiTokens.viewApiDocs')}
            </button>
            <div className="mt-6 text-gray-500 text-sm">{t('pages.apiTokens.footerLegal')}</div>
            <div className="mt-2 flex flex-col sm:flex-row justify-center gap-3 text-sm text-gray-400">
              <button
                onClick={() => navigate('/terms')}
                className="hover:text-white transition-colors"
              >
                {t('pages.apiTokens.footerTerms')}
              </button>
              <span className="hidden sm:inline text-gray-600">•</span>
              <button
                onClick={() => navigate('/privacy')}
                className="hover:text-white transition-colors"
              >
                {t('pages.apiTokens.footerPrivacy')}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default ApiTokensPage;

